import React, { useContext, useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { BusinessContext } from '../context/BusinessContext';
import { ReviewsContext } from '../context/ReviewsContext';
import { colors } from '../utils/themeUtils';

const ReviewsOverview = () => {
  const { currencySymbol, selectedBusiness } = useContext(BusinessContext);
  const { reviewsData, timeframe, view, selectedDate } = useContext(ReviewsContext);
  
  const [stats, setStats] = useState({
    reviewCount: 0,
    averageRating: 0,
    ratingChange: 0,
    revenueChange: 0,
    neededReviewsForNextRating: 0,
    nextRatingTarget: 0,
    totalReviewCount: 0,
    currentRating: 0,
    isAllReviews: false
  });

  useEffect(() => {
    // Exit if no business context
    if (!selectedBusiness) {
      return;
    }

    try {
      // Get values directly from business context
      const currentRating = selectedBusiness.rating || 0;
      const totalReviewCount = selectedBusiness.total_reviews || 0;
      
      // Process reviews data if available
      let reviewCount = 0;
      let averageRating = 0;
      let ratingChange = 0;
      let revenueChange = 0;
      let isAllReviews = false;
      
      if (reviewsData && reviewsData.length > 0) {
        // Sort reviews chronologically
        const sortedReviews = [...reviewsData].sort(
          (a, b) => new Date(a.estimated_datetime) - new Date(b.estimated_datetime)
        );
        
        // If we have a selected date, filter reviews for just that month
        let filteredReviews = sortedReviews;
        if (selectedDate) {
          filteredReviews = sortedReviews.filter(review => {
            const reviewDate = new Date(review.estimated_datetime);
            return (
              reviewDate.getFullYear() === selectedDate.getFullYear() &&
              reviewDate.getMonth() === selectedDate.getMonth()
            );
          });
        }
        
        // Calculate stats for the timeframe or selected month
        reviewCount = filteredReviews.length;
        
        // Calculate average rating
        const totalRating = filteredReviews.reduce((sum, review) => sum + review.rating, 0);
        averageRating = reviewCount > 0 ? totalRating / reviewCount : 0;
        
        // Calculate the total rating impact
        ratingChange = filteredReviews.reduce((sum, review) => sum + (review.review_impact || 0), 0);
        
        // Calculate revenue impact if business turnover is available
        if (selectedBusiness?.turnover) {
          const multiplier = selectedBusiness.turnover * 0.1;
          revenueChange = ratingChange * multiplier;
        }
        
        // Check if we're viewing all reviews
        isAllReviews = reviewCount >= totalReviewCount || timeframe === "all";
      }
      
      // Calculate the next rating band (rounded to 1 decimal place)
      const nextRatingBand = Math.ceil(currentRating * 10) / 10;
      
      // If the current rating is already at an exact 0.1 increment, go to the next 0.1 increment
      let displayTarget = currentRating === nextRatingBand 
        ? nextRatingBand + 0.1 
        : nextRatingBand;
        
      // Cap at 5.0
      if (displayTarget > 5.0) {
        displayTarget = 5.0;
      }
      
      // Calculate the actual threshold needed (0.05 below the display target)
      // For example, to display as 5.0, we need to reach 4.95
      const actualTarget = displayTarget - 0.05;
      
      // Calculate how many 5-star reviews needed using the formula:
      // Reviews * (Target - Current) / (5 - Target)
      let neededReviewsForNextRating = 0;
      
      if (currentRating < actualTarget && totalReviewCount > 0) {
        // Current rating is below the target threshold - calculate reviews needed
        // Calculate using the exact formula
        const numerator = totalReviewCount * (actualTarget - currentRating);
        const denominator = (5 - actualTarget);
        const rawResult = numerator / denominator;
        neededReviewsForNextRating = Math.ceil(rawResult);
      } else {
        // Current rating is already at or above the threshold for the next rating band
        neededReviewsForNextRating = 0;
      }

      // Special case for 5.0 rating - if currentRating is already 5.0 exactly, show 0 reviews needed
      if (currentRating === 5) {
        neededReviewsForNextRating = 0;
      }

      // If we're already above the target threshold, calculate the next higher target
      // This avoids showing "You need 0 reviews to reach X" which doesn't make sense
      if (neededReviewsForNextRating === 0 && currentRating < 5 && totalReviewCount > 0) {
        // We need to find the next higher rating band
        const nextHigherTarget = Math.ceil(displayTarget * 10 + 1) / 10;
        const nextHigherThreshold = nextHigherTarget - 0.05;
        
        // Calculate reviews needed to reach the next higher target
        const numerator = totalReviewCount * (nextHigherThreshold - currentRating);
        const denominator = (5 - nextHigherThreshold);
        const rawResult = numerator / denominator;
        neededReviewsForNextRating = Math.ceil(rawResult);
        
        // Update the display target to the next higher band
        setStats({
          reviewCount,
          averageRating,
          ratingChange,
          revenueChange,
          neededReviewsForNextRating,
          nextRatingTarget: nextHigherTarget,
          totalReviewCount,
          currentRating,
          isAllReviews
        });
        return;
      }

      setStats({
        reviewCount,
        averageRating,
        ratingChange,
        revenueChange,
        neededReviewsForNextRating,
        nextRatingTarget: displayTarget,
        totalReviewCount,
        currentRating,
        isAllReviews
      });
    } catch (error) {
      console.error("Error calculating reviews stats:", error);
    }
  }, [reviewsData, timeframe, selectedBusiness, selectedDate]);

  // Format number to one decimal place
  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0';
    return Number(num).toFixed(1);
  };

  // Format impact values with dynamic precision to match DashboardOverview
  const formatImpact = (impact) => {
    if (impact === undefined || impact === null) return '0.000';
    
    const absImpact = Math.abs(impact);
    
    // For zero values
    if (absImpact === 0) return '0.000';
    
    // For normal values (≥ 0.001), use standard 3 decimal places
    if (absImpact >= 0.001) {
      return absImpact.toFixed(3);
    }
    
    // For very small values, find the first significant digit
    const scientificNotation = absImpact.toExponential();
    
    // Parse the scientific notation (e.g., "3.45e-6")
    const matches = scientificNotation.match(/^(\d)\.?(\d*)?e-(\d+)$/);
    
    if (matches) {
      const firstDigit = matches[1]; // First significant digit
      const exponent = parseInt(matches[3], 10);
      
      // Create the string with leading zeros
      let result = '0.';
      for (let i = 1; i < exponent; i++) {
        result += '0';
      }
      result += firstDigit;
      
      return result;
    }
    
    // Fallback to standard formatting
    return absImpact.toFixed(3);
  };

  // Format currency values
  const formatCurrency = (value) => {
    if (value === undefined || value === null) return '0';
    
    // For values less than 1
    if (Math.abs(value) < 1) {
      return Math.abs(value).toFixed(2);
    }
    
    // For values greater than or equal to 1
    return Math.abs(value).toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  // Get timeframe in days or "all time"
  const getTimeframeDays = () => {
    if (selectedDate) {
      return selectedDate.toLocaleString('default', { month: 'long', year: 'numeric' });
    } else if (timeframe === "all") {
      return "all time";
    } else {
      return `last ${timeframe} days`;
    }
  };
  
  // Determine if we're displaying stats for a selected month
  const isMonthSelected = !!selectedDate;

  return (
    <Box sx={{ 
      height: '100%',
      p: { xs: 2, md: 0 },
      overflow: 'auto'
    }}>
      <Typography variant="body1" sx={{ fontSize: { xs: '1rem', md: '1.125rem' }, lineHeight: 1.7, mt: 0 }}>
        {stats.reviewCount === 0 ? (
          <>
            {isMonthSelected ? "In" : timeframe === "all" ? "Over" : "In the"}{' '}
            <Box component="span" sx={{ fontWeight: 'bold', color: 'black' }}>
              {getTimeframeDays()}
            </Box>
            {', '}
            you {isMonthSelected ? '' : 'have '}received{' '}
            <Box component="span" sx={{ fontWeight: 'bold', color: 'black' }}>
              0
            </Box>
            {' '}reviews
          </>
        ) : (
          <>
            {isMonthSelected ? "In" : timeframe === "all" ? "Over" : "In the"}{' '}
            <Box component="span" sx={{ fontWeight: 'bold', color: 'black' }}>
              {getTimeframeDays()}
            </Box>
            {', '}
            you {isMonthSelected ? '' : 'have '}received{' '}
            <Box component="span" sx={{ fontWeight: 'bold', color: 'black' }}>
              {stats.reviewCount}
            </Box>
            {' '}reviews with an average rating of{' '}
            <Box 
              component="span" 
              sx={{ 
                fontWeight: 'bold', 
                color: stats.averageRating > stats.currentRating 
                  ? colors.positiveGreen
                  : stats.averageRating < stats.currentRating 
                  ? colors.negativeRed 
                  : 'black',
                whiteSpace: 'nowrap'
              }}
            >
              {formatNumber(stats.averageRating)} ★
            </Box>
            {(!stats.isAllReviews || isMonthSelected) && stats.ratingChange !== 0 && (
              <>
                {'. '}
                This has impacted your overall{' '}
                <Box component="span" sx={{ fontWeight: 'bold', color: 'black' }}>
                  {view === "Sales" ? "revenue" : "rating"}
                </Box>
                {' '}by{' '}
                <Box 
                  component="span" 
                  sx={{ 
                    fontWeight: 'bold', 
                    color: (view === "Sales" ? stats.revenueChange : stats.ratingChange) >= 0 
                      ? colors.positiveGreen 
                      : colors.negativeRed,
                    whiteSpace: 'nowrap'
                  }}
                >
                  {(view === "Sales" ? stats.revenueChange : stats.ratingChange) >= 0 ? '+' : '-'}
                  {view === "Sales" 
                    ? `${currencySymbol}${formatCurrency(Math.abs(stats.revenueChange))}` 
                    : `${formatImpact(Math.abs(stats.ratingChange))} ★`}
                </Box>
              </>
            )}
          </>
        )}
        {'.'}
      </Typography>
      
      {/* Only show required reviews text if rating is below 4.95 */}
      {stats.currentRating < 4.95 && (
        <Typography variant="body1" sx={{ fontSize: { xs: '1rem', md: '1.125rem' }, lineHeight: 1.7, mt: 2 }}>
          You need{' '}
          <Box component="span" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            {stats.neededReviewsForNextRating}
          </Box>
          {' '}5-star reviews to reach a{' '}
          <Box component="span" sx={{ fontWeight: 'bold', color: 'black', whiteSpace: 'nowrap' }}>
            {formatNumber(stats.nextRatingTarget)} ★
          </Box>
          {' '}rating.
        </Typography>
      )}
    </Box>
  );
};

export default ReviewsOverview; 